import React, {useEffect, useMemo, useState} from 'react';
import {Profile} from '../../types/api';
import {
    getProfileQrCodeUrl,
    logProfileView,
} from '../../services/api/ProfileApi';
import ProfileView from '../../components/Profile/ProfileView';
import {ThemeProvider} from '@mui/material';
import {createCachedTheme} from '../../components/Ui/themes/ProfileTheme';
import MediumDisabled from './MediumDisabled';
import ProfileMenu from '../../components/Profile/ProfileMenu';
import {useTranslation} from 'react-i18next';
import {getLocalStorage} from '../../lib/storage';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import ModalStack from '../../hooks/useModalStack';
import {useGtm} from '../../components/useGtm';
import {useLoaderData, useLocation, useNavigate} from 'react-router-dom';
import {useParams} from '@alchemy/navigation';
import {AppGlobalStyles} from '../../style.tsx';
import {Helmet} from 'react-helmet';

export default function ProfileShow() {
    const {safeKey} = useParams();
    const profile = useLoaderData() as Profile;
    const {search} = useLocation();
    const navigate = useNavigate();
    const {id} = profile;
    const [error, _setError] = useState<number>();
    const {t, i18n} = useTranslation('app');
    const {trackPageView} = useMatomo();

    useEffect(() => {
        trackPageView();
        getLocalStorage()?.removeItem('i18nextLng');
        if (profile.options.autoOpenForm) {
            navigate('?exchange', {replace: true});
        }
    }, []);

    const theme = useMemo(() => {
        return createCachedTheme(
            profile?.resolvedTheme?.name ?? 'default',
            i18n.dir(),
        );
    }, [profile, i18n]);

    useEffect(() => {
        logProfileView(profile.id);
        if (
            profile.vcfUrl &&
            new URLSearchParams(window.location.search).get('vcf')
        ) {
            setTimeout(() => {
                document.location.href = profile.vcfUrl!;
            }, 1500);
        }
    }, [id]);

    const qrOnlineUrl = getProfileQrCodeUrl(id!, safeKey, {
        offline: false,
    });
    const qrOfflineUrl = getProfileQrCodeUrl(id!, safeKey, {
        offline: true,
        vcf: {
            avatar: false,
            note: false,
            links: false,
            addresses: false,
        },
    });

    useEffect(() => {
        const preLoadImage = (url: string) => {
            const img = new Image();
            img.src = url;
        };
        preLoadImage(qrOnlineUrl);
        preLoadImage(qrOfflineUrl);
    }, [id]);

    const {trackEvent} = useMatomo();

    const track = React.useCallback(
        (open: boolean) => {
            trackEvent({
                category: 'interaction',
                action: 'toggle',
                name: 'exchange',
                value: open ? 1 : 0,
            });
        },
        [trackEvent],
    );

    const showExchangeModal = React.useCallback(() => {
        navigate('?exchange');
        track(true);
    }, [track]);

    const closeExchangeModal = React.useCallback(() => {
        navigate('', {replace: true});
        track(false);
    }, [track]);

    useGtm({
        enabled: !!profile?.follow,
    });

    if (error && [404, 403].includes(error)) {
        return <MediumDisabled />;
    }

    if (profile && profile.expired) {
        return <MediumDisabled />;
    }

    return (
        <ThemeProvider theme={theme}>
            {AppGlobalStyles}
            <Helmet encodeSpecialCharacters={false}>
                <title>{profile.profileLabel}</title>
                <link rel="manifest" href={profile.manifestUrl} />
                <meta
                    property="og:title"
                    content={profile.ogTitle ?? profile.profileLabel}
                />
                <meta
                    property="og:description"
                    content={
                        profile.ogDescription ??
                        t(
                            'app:og.default_description',
                            'Get my contact details on VKARD',
                        )
                    }
                />
                <meta property="og:type" content="profile" />
                {profile.ogImageUrl ? (
                    <meta property="og:image" content={profile.ogImageUrl} />
                ) : null}
            </Helmet>
            <ModalStack>
                <ProfileMenu
                    profile={profile}
                    safeKey={safeKey!}
                    qrOnline={qrOnlineUrl}
                    qrOffline={qrOfflineUrl}
                />
                <ProfileView
                    profile={profile}
                    safeKey={safeKey!}
                    showExchange={showExchangeModal}
                    closeExchange={closeExchangeModal}
                    displayExchange={search.includes('exchange')}
                />
            </ModalStack>
        </ThemeProvider>
    );
}
